"use client";

import { Flower2, Leaf, Sun, Moon, Sparkles, Coffee, Tag, Users, Calendar, Bath, BookOpen, Heart, Star, Gift, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Maintenance() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const categories = [{
    title: "System Optimization",
    benefits: [{
      icon: Wind,
      name: "Filter Replacement",
      description: "Regular cleaning and replacement to ensure optimal airflow and air quality."
    }, {
      icon: Sun,
      name: "Coil Cleaning",
      description: "Deep cleaning of evaporator and condenser coils for maximum efficiency."
    }, {
      icon: Sparkles,
      name: "Drain Line Flushing",
      description: "Clearing blockages to prevent water damage and mold growth."
    }, {
      icon: Star,
      name: "Refrigerant Level Check",
      description: "Ensuring your system operates at the correct pressure for cooling performance."
    }]
  }, {
    title: "Technical Inspections",
    benefits: [{
      icon: Moon,
      name: "Electrical Component Test",
      description: "Inspecting wiring and connections to prevent unexpected system failures."
    }, {
      icon: Tag,
      name: "Thermostat Calibration",
      description: "Ensuring precise temperature control for your home or office environment."
    }, {
      icon: Heart,
      name: "Motor & Fan Inspection",
      description: "Checking moving parts for wear and tear to extend equipment lifespan."
    }, {
      icon: Leaf,
      name: "Energy Efficiency Audit",
      description: "Optimizing settings to reduce your monthly energy consumption."
    }, {
      icon: Coffee,
      name: "System Performance Report",
      description: "Detailed documentation of your unit's health and recommended actions."
    }]
  }, {
    title: "Priority Support",
    benefits: [{
      icon: Gift,
      name: "Emergency Call-out Priority",
      description: "Members receive front-of-the-line service during peak summer heat."
    }, {
      icon: Calendar,
      name: "Scheduled Maintenance Reminders",
      description: "We track your service intervals so you never miss a check-up."
    }, {
      icon: Users,
      name: "Dedicated Account Manager",
      description: "A single point of contact for all your climate control needs."
    }, {
      icon: BookOpen,
      name: "Extended Warranty Support",
      description: "Assistance with manufacturer warranty claims and documentation."
    }, {
      icon: Star,
      name: "Discounted Repair Rates",
      description: "Exclusive member pricing on all parts and labor for repairs."
    }]
  }];
  return <section data-section-id="158" id="maintenanceservices" className="bg-background py-[140px] px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-primary mb-6">
            Service Packages
          </p>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[52px] leading-tight text-foreground">
            Beyond the <em className="italic text-primary">cooling.</em>
          </h2>
          <div className="w-12 h-px bg-border mx-auto mt-8" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={imageRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24 overflow-hidden max-w-full rounded-lg shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/ac_maintenance_technician.webp" alt="Professional technician performing a detailed inspection on a commercial air conditioning unit" loading="lazy" decoding="async" className="w-full h-[300px] lg:h-[400px] object-cover" />
            </div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-14">
              {categories.map((category, catIndex) => <div data-index={catIndex} key={catIndex}>
                  {catIndex > 0 && <div className="w-full h-px bg-border mb-14" aria-hidden="true" />}

                  <h3 className="font-default font-semibold text-2xl text-foreground mb-8 italic">
                    {category.title}
                  </h3>

                  <ul className="space-y-6">
                    {category.benefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return <li data-index={i} key={i} className="group flex items-start gap-5 cursor-default">
                          <div className="shrink-0 mt-1">
                            <Icon className="w-5 h-5 text-secondary transition-all duration-500 ease-out group-hover:scale-125" strokeWidth={1.25} aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            
                            <p className="font-sans text-sm text-muted-foreground mt-1 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </li>;
                })}
                  </ul>
                </div>)}
            </div>

            {}
            <div className="mt-16 pt-10 border-t border-border">
              <p className="font-default font-medium text-xl italic text-muted-foreground leading-relaxed">
                "Reliable climate control is more than just hardware—it is the peace of mind that comes from professional care and consistent performance."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}